import type { Identity } from '../../core/auth/session.js';

/**
 * Подсказки новичку (3.16.10): четыре на большом экране, пять на телефоне.
 *
 * Только главное — записать дело, закрыть и сменить статус, недели, Входящие.
 * Остальное (проекты, теги, сроки, чек-листы) обещано инструкцией: всё сразу
 * пугало, и человек не понимал, с чего начать (замечание владельца,
 * 2026-09-13).
 *
 * Тексты у телефона свои, и это не стилистика: статус в списке меняют кнопкой,
 * а перетаскивание там — долгим нажатием (3.1.11), кнопки «Перенести в
 * следующий» нет, а с мышью на доске галочка видна только под курсором
 * (3.1.10). Подсказка, которая называет несуществующую кнопку, хуже
 * отсутствия подсказки.
 */

/** Что подсвечивается, пока подсказка на экране: стили цепляются к `data-tip`. */
export type TipTarget = 'quick' | 'status' | 'weeks' | 'inbox';

export type Platform = 'ios' | 'android' | 'other';

export type Browser = 'safari' | 'chrome' | 'firefox' | 'edge' | 'yandex' | 'samsung' | 'other';

export interface BrowserInfo {
  platform: Platform;
  browser: Browser;
  /** Встроенный браузер другого приложения — почты, мессенджера. */
  inApp: boolean;
}

export interface Tip {
  id: 'add' | 'status' | 'weeks' | 'inbox' | 'home';
  title: string;
  body: string;
  steps?: readonly string[];
  highlight?: TipTarget;
}

export const FINAL_NOTE =
  'Это главное. Про проекты, теги, сроки и чек-листы расскажем в инструкции — она скоро появится.';

/** Сколько учётная запись считается новой. Дальше подсказки уже не к месту. */
export const NEW_ACCOUNT_DAYS = 14;

const STORAGE_KEY = 'nedelnik.tips';

export function isNewAccount(identity: Identity | undefined, now: Date): boolean {
  const created = identity?.created_at;
  if (created === undefined) return false;

  const at = Date.parse(created);
  return Number.isFinite(at) && now.getTime() - at < NEW_ACCOUNT_DAYS * 86_400_000;
}

/**
 * Скрытые подсказки помнит устройство, а не учётная запись.
 *
 * Синхронизировать это незачем: на телефоне интерфейс другой, и увидеть там
 * подсказки заново — скорее польза. Под `try/catch` по той же причине, что и у
 * темы: в приватном окне `localStorage` бросает исключение.
 */
export function readTipsDismissed(): boolean {
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) === 'done';
  } catch {
    return false;
  }
}

export function dismissTips(): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, 'done');
  } catch {
    // Не запомнилось — подсказки покажутся после перезагрузки ещё раз, и только.
  }
}

/** Открыто ли приложение со значка на экране «Домой», а не во вкладке браузера. */
export function isStandalone(): boolean {
  const media = globalThis.matchMedia?.('(display-mode: standalone)').matches ?? false;
  // Safari на iOS отвечает на медиазапрос не всегда, зато ставит свой признак.
  const ios = (globalThis.navigator as { standalone?: boolean } | undefined)?.standalone === true;
  return media || ios;
}

/**
 * Какой браузер — по строке `User-Agent`.
 *
 * Нужно ровно для одного: назвать кнопку и пункт меню так, как они называются
 * у человека на экране. Ошибка распознавания здесь не опасна — неизвестный
 * браузер получает общие шаги.
 *
 * Встроенный браузер приложения на iOS обычно не пишет `Safari/` в конце —
 * у настоящих браузеров, включая Chrome, он есть. На Android встроенный
 * WebView помечает себя `; wv)`. Распознаётся не всякий: часть приложений
 * открывает страницы в окне Safari, неотличимом по строке, — поэтому в шагах
 * для iPhone есть запасной выход «откройте в Safari».
 */
export function browserOf(userAgent: string): BrowserInfo {
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    const browser: Browser = /CriOS/.test(userAgent)
      ? 'chrome'
      : /FxiOS/.test(userAgent)
        ? 'firefox'
        : /EdgiOS/.test(userAgent)
          ? 'edge'
          : /YaBrowser/.test(userAgent)
            ? 'yandex'
            : 'safari';
    return { platform: 'ios', browser, inApp: !/Safari\//.test(userAgent) };
  }

  if (/Android/i.test(userAgent)) {
    const browser: Browser = /SamsungBrowser/.test(userAgent)
      ? 'samsung'
      : /EdgA/.test(userAgent)
        ? 'edge'
        : /YaBrowser/.test(userAgent)
          ? 'yandex'
          : /Firefox/.test(userAgent)
            ? 'firefox'
            : /Chrome\//.test(userAgent)
              ? 'chrome'
              : 'other';
    return { platform: 'android', browser, inApp: /; wv\)/.test(userAgent) };
  }

  return { platform: 'other', browser: 'other', inApp: false };
}

export function tipsFor(context: {
  narrow: boolean;
  mouse: boolean;
  standalone: boolean;
  browser: BrowserInfo;
}): Tip[] {
  const { narrow, mouse } = context;

  const tips: Tip[] = [
    {
      id: 'add',
      title: 'Запишите дело строкой сверху',
      body: narrow
        ? 'Напишите и нажмите «+» — задача появится в этой неделе.'
        : 'Напишите и нажмите Enter — задача появится в этой неделе.',
      highlight: 'quick',
    },
    {
      id: 'status',
      // Кнопки названы словами, а не значками ▷ и ⏸: символа паузы нет в части
      // шрифтов, и на телефоне вместо него стоял пустой квадрат. Какая кнопка
      // имеется в виду, показывает подсветка.
      title: narrow
        ? 'Галочка — готово, соседняя кнопка — статус'
        : 'Галочка — готово, перетаскивание — статус',
      body: narrow
        ? 'Кнопка слева от галочки берёт задачу в работу, а у задачи в работе — возвращает в бэклог. Галочка закрывает. Задачу можно и переставить: удержите палец на ней полсекунды и тащите. Попробуйте на примерах ниже.'
        : mouse
          ? 'Наведите на карточку — справа появится галочка, она закрывает задачу. Чтобы взять задачу в работу или вернуть, перетащите карточку в соседнюю колонку.'
          : 'Галочка закрывает задачу, кнопка слева от неё берёт в работу или возвращает в бэклог. Карточку можно и перетащить в соседнюю колонку.',
      highlight: 'status',
    },
    {
      id: 'weeks',
      title: 'Неделя — это спринт',
      body: narrow
        ? 'У каждой недели свой список, стрелки у названия листают недели. Чтобы поставить задачу в другую неделю, откройте её и выберите неделю.'
        : 'У каждой недели свой список, стрелки у названия листают недели. Незакрытое в конце недели переносится в следующую одной кнопкой.',
      highlight: 'weeks',
    },
    {
      id: 'inbox',
      title: 'Входящие — всё без недели',
      body: narrow
        ? 'Значок справа вверху — Входящие. Там уже лежит одна идея: откройте её и поставьте в неделю.'
        : 'Кнопка «Входящие» справа вверху. Там уже лежит одна идея: откройте её и поставьте в неделю.',
      highlight: 'inbox',
    },
  ];

  // Экран «Домой» — только на телефоне и только из браузера: со значка
  // приложение уже открыто, и объяснять, как его туда поставить, — насмешка.
  if (narrow && !context.standalone) tips.push(homeScreenTip(context.browser));

  return tips;
}

const HOME_TITLE = 'Добавьте Onlap на экран «Домой»';

/**
 * Зачем приложение на экране «Домой» — не очевидно, поэтому сначала польза,
 * потом шаги.
 *
 * Шаги названы под браузер: пункт меню у всех свой, и общий текст заставлял бы
 * искать то, чего на экране нет. Значки меню описаны словами («три точки»):
 * символов ⋮ и ⋯ нет в части шрифтов — та же беда, что с паузой.
 *
 * На iPhone у приложения со значка своё хранилище, отдельное от браузера, —
 * войти там придётся заново, и сказать это надо заранее: иначе экран входа
 * выглядит как потерянная учётная запись.
 */
function homeScreenTip(info: BrowserInfo): Tip {
  if (info.inApp) {
    return {
      id: 'home',
      title: HOME_TITLE,
      body: 'Сейчас Onlap открыт внутри другого приложения — например, почты или мессенджера, — и отсюда на экран «Домой» его не добавить.',
      steps: [
        info.platform === 'ios'
          ? 'В меню этого приложения выберите «Открыть в Safari» или «Открыть в браузере».'
          : 'В меню этого приложения выберите «Открыть в браузере».',
        'Там откройте onlap.app — подсказка покажет, что делать дальше.',
      ],
    };
  }

  return {
    id: 'home',
    title: HOME_TITLE,
    body: 'Появится значок, как у обычного приложения: Onlap будет открываться в одно касание, без адресной строки, работать без сети, а войти можно будет отпечатком или лицом вместо кода из письма.',
    steps: homeScreenSteps(info),
  };
}

function homeScreenSteps(info: BrowserInfo): readonly string[] {
  if (info.platform === 'ios') {
    const share =
      info.browser === 'safari'
        ? 'Нажмите «Поделиться» — квадрат со стрелкой вверх внизу экрана или рядом с адресной строкой.'
        : info.browser === 'chrome'
          ? 'Нажмите «Поделиться» — значок в адресной строке.'
          : 'Нажмите «Поделиться» — в адресной строке или в меню браузера.';

    return [
      share,
      'Выберите «На экран Домой» и нажмите «Добавить». Нет такого пункта — откройте onlap.app в Safari и повторите.',
      'Откройте Onlap со значка и войдите ещё раз — это нужно один раз.',
    ];
  }

  if (info.platform === 'android') {
    const menu: Record<Browser, readonly string[]> = {
      chrome: [
        'Нажмите на три точки справа вверху.',
        'Выберите «Установить приложение» или «Добавить на главный экран».',
      ],
      samsung: [
        'Нажмите на меню — три полоски внизу справа.',
        'Выберите «Добавить страницу на», затем «Главный экран».',
      ],
      firefox: ['Нажмите на три точки.', 'Выберите «Установить» или «Добавить на домашний экран».'],
      edge: [
        'Откройте меню браузера — три точки.',
        'Выберите «Добавить на телефон» или «Установить приложение».',
      ],
      yandex: [
        'Откройте меню браузера — три точки.',
        'Найдите «Добавить на главный экран» или «Установить приложение».',
      ],
      safari: [],
      other: [
        'Откройте меню браузера — обычно это три точки.',
        'Найдите «Установить приложение» или «Добавить на главный экран».',
      ],
    };

    return [...menu[info.browser], 'Открывайте Onlap со значка на главном экране.'];
  }

  return [
    'iPhone: в браузере «Поделиться» → «На экран Домой», затем войдите со значка ещё раз.',
    'Android: в меню браузера — «Установить приложение» или «Добавить на главный экран».',
  ];
}
